import { d as colors } from "./focus-DaIphPOd.mjs";
import { c as transitions, d as spacing, f as zIndices, h as fonts, l as radii, m as fontWeights, n as durations, p as fontSizes, u as shadows, v as typography } from "./motion-Cg-JnSSP.mjs";
import { r as useControllableState } from "./hooks-ChkgkdFK.mjs";
import { n as Label, t as ThemeSwatch } from "./ThemeSwatch-JAmwSMrS.mjs";
import { i as THEMES, n as useTheme } from "./context-BiO95ZTa.mjs";
import React, { createContext, forwardRef, useCallback, useContext, useEffect, useId, useRef, useState } from "react";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
//#region molecules/Tabs.tsx
/**
* EITELKEIT Tabs
* ═══════════════════════════════════════════════════════════════════
* Tab navigation with mint underline
* Full ARIA support and keyboard navigation (WCAG 2.1.1, 4.1.2)
*/
const TabsContext = createContext(null);
function useTabsContext() {
	const context = useContext(TabsContext);
	if (!context) throw new Error("Tabs components must be used within Tabs");
	return context;
}
function Tabs({ value, defaultValue, onChange, children, "aria-label": ariaLabel }) {
	const [activeValue, setActiveValue] = useControllableState(value, defaultValue ?? "", onChange ?? void 0);
	const baseId = useId();
	return /* @__PURE__ */ jsx(TabsContext.Provider, {
		value: {
			value: activeValue,
			onChange: setActiveValue,
			baseId
		},
		children: /* @__PURE__ */ jsx("div", {
			role: "group",
			"aria-label": ariaLabel ?? void 0,
			children
		})
	});
}
Tabs.List = function TabList({ style, children, "aria-label": ariaLabel, ...props }) {
	const label = ariaLabel ?? "Tab Navigation";
	const listRef = useRef(null);
	const { value, onChange } = useTabsContext();
	const getTabValues = useCallback(() => {
		const values = [];
		React.Children.forEach(children, (child) => {
			if (React.isValidElement(child) && child.props.value) values.push(child.props.value);
		});
		return values;
	}, [children]);
	return /* @__PURE__ */ jsx("div", {
		ref: listRef,
		role: "tablist",
		"aria-label": label,
		onKeyDown: useCallback((e) => {
			const tabValues = getTabValues();
			const currentIndex = tabValues.indexOf(value);
			let nextIndex = currentIndex;
			let shouldPrevent = true;
			switch (e.key) {
				case "ArrowRight":
				case "ArrowDown":
					nextIndex = (currentIndex + 1) % tabValues.length;
					break;
				case "ArrowLeft":
				case "ArrowUp":
					nextIndex = (currentIndex - 1 + tabValues.length) % tabValues.length;
					break;
				case "Home":
					nextIndex = 0;
					break;
				case "End":
					nextIndex = tabValues.length - 1;
					break;
				default: shouldPrevent = false;
			}
			if (shouldPrevent) {
				e.preventDefault();
				const nextValue = tabValues[nextIndex];
				if (nextValue !== void 0) {
					onChange(nextValue);
					((listRef.current?.querySelectorAll("[role=\"tab\"]"))?.[nextIndex])?.focus();
				}
			}
		}, [
			value,
			onChange,
			getTabValues
		]),
		style: {
			display: "flex",
			gap: "24px",
			borderBottom: `1px solid ${colors.border}`,
			...style
		},
		...props,
		children
	});
};
Tabs.Tab = function Tab({ value, style, children, onFocus, onBlur, ...props }) {
	const { value: activeValue, onChange, baseId } = useTabsContext();
	const isActive = value === activeValue;
	const [isFocused, setIsFocused] = useState(false);
	const tabId = `${baseId}-tab-${value}`;
	const panelId = `${baseId}-panel-${value}`;
	const handleFocus = (e) => {
		setIsFocused(true);
		onFocus?.(e);
	};
	const handleBlur = (e) => {
		setIsFocused(false);
		onBlur?.(e);
	};
	return /* @__PURE__ */ jsxs("button", {
		role: "tab",
		id: tabId,
		"aria-selected": isActive,
		"aria-controls": panelId,
		tabIndex: isActive ? 0 : -1,
		onClick: () => onChange(value),
		onFocus: handleFocus,
		onBlur: handleBlur,
		style: {
			background: "transparent",
			border: "none",
			padding: "12px 0",
			fontFamily: fonts.sans,
			fontSize: "14px",
			fontWeight: isActive ? 600 : 400,
			color: isActive ? colors.mint : colors.textMuted,
			cursor: "pointer",
			transition: transitions.all,
			position: "relative",
			outline: "none",
			boxShadow: isFocused ? shadows.focusRing : "none",
			borderRadius: "4px 4px 0 0",
			...style
		},
		...props,
		children: [children, isActive && /* @__PURE__ */ jsx("span", {
			style: {
				position: "absolute",
				bottom: "-1px",
				left: 0,
				right: 0,
				height: "2px",
				background: colors.mint,
				borderRadius: "1px 1px 0 0"
			},
			"aria-hidden": "true"
		})]
	});
};
Tabs.Panel = function TabPanel({ value, style, children, ...props }) {
	const { value: activeValue, baseId } = useTabsContext();
	const tabId = `${baseId}-tab-${value}`;
	const panelId = `${baseId}-panel-${value}`;
	if (value !== activeValue) return null;
	return /* @__PURE__ */ jsx("div", {
		role: "tabpanel",
		id: panelId,
		"aria-labelledby": tabId,
		tabIndex: 0,
		style: {
			paddingTop: "16px",
			outline: "none",
			...style
		},
		...props,
		children
	});
};
//#endregion
//#region molecules/FormField.tsx
function FormField({ label, required, error, hint, children, id }) {
	return /* @__PURE__ */ jsxs("div", {
		style: { marginBottom: "16px" },
		children: [
			label && /* @__PURE__ */ jsx(Label, {
				htmlFor: id ?? void 0,
				required,
				variant: null,
				children: label
			}),
			children,
			error && /* @__PURE__ */ jsx("p", {
				style: {
					marginTop: "6px",
					fontSize: "12px",
					color: colors.danger
				},
				children: error
			}),
			hint && !error && /* @__PURE__ */ jsx("p", {
				style: {
					marginTop: "6px",
					fontSize: "12px",
					color: colors.textMuted
				},
				children: hint
			})
		]
	});
}
//#endregion
//#region molecules/StatDisplay.tsx
const sizes = {
	sm: {
		value: "16px",
		label: "9px"
	},
	md: {
		value: "24px",
		label: "10px"
	},
	lg: {
		value: "32px",
		label: "11px"
	}
};
const trendColors = {
	up: colors.mint,
	down: colors.danger,
	neutral: colors.textMuted
};
function StatDisplay({ value, label, trend, trendValue, size, style, ...props }) {
	const sz = size ?? "md";
	const s = sizes[sz];
	return /* @__PURE__ */ jsxs("div", {
		style: {
			background: colors.voidLight,
			padding: sz === "sm" ? "8px" : "12px",
			borderRadius: radii.sm,
			textAlign: "center",
			...style
		},
		...props,
		children: [/* @__PURE__ */ jsxs("div", {
			style: {
				fontFamily: fonts.mono,
				fontWeight: 600,
				fontSize: s.value,
				color: colors.mint,
				marginBottom: "4px"
			},
			children: [value, trend && trendValue && /* @__PURE__ */ jsxs("span", {
				style: {
					fontSize: "12px",
					marginLeft: "6px",
					color: trendColors[trend]
				},
				children: [
					trend === "up" ? "↑" : trend === "down" ? "↓" : "→",
					" ",
					trendValue
				]
			})]
		}), /* @__PURE__ */ jsx("div", {
			style: {
				...typography.label,
				fontSize: s.label,
				color: colors.textMuted
			},
			children: label
		})]
	});
}
//#endregion
//#region molecules/Modal.tsx
/**
* EITELKEIT Modal
* ═══════════════════════════════════════════════════════════════════
* Accessible modal dialog with backdrop blur and keyboard support
*/
const sizeMap = {
	sm: "400px",
	md: "500px",
	lg: "640px",
	xl: "800px"
};
function Modal({ isOpen, onClose, title, size, children, style }) {
	const [isCloseHovered, setIsCloseHovered] = React.useState(false);
	const s = size ?? "md";
	const handleKeyDown = useCallback((event) => {
		if (event.key === "Escape") onClose();
	}, [onClose]);
	useEffect(() => {
		if (isOpen) {
			document.addEventListener("keydown", handleKeyDown);
			document.body.style.overflow = "hidden";
		}
		return () => {
			document.removeEventListener("keydown", handleKeyDown);
			document.body.style.overflow = "";
		};
	}, [isOpen, handleKeyDown]);
	const handleBackdropClick = (event) => {
		if (event.target === event.currentTarget) onClose();
	};
	if (!isOpen) return null;
	const backdropStyle = {
		position: "fixed",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		backgroundColor: colors.surfaceOverlay,
		backdropFilter: "blur(4px)",
		WebkitBackdropFilter: "blur(4px)",
		zIndex: zIndices.modal,
		padding: spacing.lg
	};
	const dialogStyle = {
		position: "relative",
		width: "100%",
		maxWidth: sizeMap[s],
		backgroundColor: colors.surface,
		border: `1px solid ${colors.border}`,
		borderRadius: radii.lg,
		boxShadow: shadows.lg,
		maxHeight: "90vh",
		overflow: "hidden",
		display: "flex",
		flexDirection: "column",
		...style
	};
	const headerStyle = {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		padding: `${spacing.md} ${spacing.lg}`,
		borderBottom: `1px solid ${colors.border}`
	};
	const titleStyle = {
		margin: 0,
		fontFamily: fonts.sans,
		fontSize: fontSizes.lg,
		fontWeight: fontWeights.semibold,
		color: colors.text
	};
	const closeButtonStyle = {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		width: "32px",
		height: "32px",
		padding: 0,
		background: isCloseHovered ? colors.surfaceElevated : "transparent",
		border: "none",
		borderRadius: radii.sm,
		cursor: "pointer",
		color: isCloseHovered ? colors.text : colors.textMuted,
		transition: transitions.all
	};
	const contentStyle = {
		padding: spacing.lg,
		overflowY: "auto",
		flex: 1,
		color: colors.text,
		fontFamily: fonts.sans,
		fontSize: fontSizes.base
	};
	return /* @__PURE__ */ jsx("div", {
		"data-testid": "modal-backdrop",
		style: backdropStyle,
		onClick: handleBackdropClick,
		children: /* @__PURE__ */ jsxs("div", {
			role: "dialog",
			"aria-modal": "true",
			"aria-labelledby": "modal-title",
			style: dialogStyle,
			children: [/* @__PURE__ */ jsxs("header", {
				style: headerStyle,
				children: [/* @__PURE__ */ jsx("h2", {
					id: "modal-title",
					style: titleStyle,
					children: title
				}), /* @__PURE__ */ jsx("button", {
					type: "button",
					onClick: onClose,
					onMouseEnter: () => setIsCloseHovered(true),
					onMouseLeave: () => setIsCloseHovered(false),
					style: closeButtonStyle,
					"aria-label": "Close modal",
					children: /* @__PURE__ */ jsxs("svg", {
						width: "16",
						height: "16",
						viewBox: "0 0 16 16",
						fill: "none",
						stroke: "currentColor",
						strokeWidth: "2",
						strokeLinecap: "round",
						strokeLinejoin: "round",
						children: [/* @__PURE__ */ jsx("line", {
							x1: "4",
							y1: "4",
							x2: "12",
							y2: "12"
						}), /* @__PURE__ */ jsx("line", {
							x1: "12",
							y1: "4",
							x2: "4",
							y2: "12"
						})]
					})
				})]
			}), /* @__PURE__ */ jsx("div", {
				style: contentStyle,
				children
			})]
		})
	});
}
//#endregion
//#region molecules/TreeView.tsx
/**
* EITELKEIT TreeView
* ═══════════════════════════════════════════════════════════════════
* Hierarchical tree component with expandable nodes
* Full ARIA support for accessibility (WCAG 2.1)
*/
function ChevronIcon({ expanded }) {
	return /* @__PURE__ */ jsx("svg", {
		width: "16",
		height: "16",
		viewBox: "0 0 16 16",
		fill: "none",
		style: {
			transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
			transition: transitions.fast,
			flexShrink: 0
		},
		"aria-hidden": "true",
		children: /* @__PURE__ */ jsx("path", {
			d: "M6 4L10 8L6 12",
			stroke: colors.textMuted,
			strokeWidth: "1.5",
			strokeLinecap: "round",
			strokeLinejoin: "round"
		})
	});
}
function TreeNodeItem({ node, level, selectedId, onSelect, expandedIds, onToggle }) {
	const [isHovered, setIsHovered] = useState(false);
	const hasChildren = node.children !== null && node.children.length > 0;
	const isExpanded = expandedIds.has(node.id);
	const isSelected = selectedId === node.id;
	const handleClick = useCallback(() => {
		if (hasChildren) onToggle(node.id);
		if (onSelect) onSelect(node.id);
	}, [
		hasChildren,
		node.id,
		onSelect,
		onToggle
	]);
	return /* @__PURE__ */ jsxs("div", {
		role: "treeitem",
		"aria-expanded": hasChildren ? isExpanded : void 0,
		"aria-selected": isSelected,
		children: [/* @__PURE__ */ jsxs("div", {
			onClick: handleClick,
			onMouseEnter: () => setIsHovered(true),
			onMouseLeave: () => setIsHovered(false),
			style: {
				display: "flex",
				alignItems: "center",
				gap: "8px",
				padding: "6px 8px",
				paddingLeft: `${level * 20 + 8}px`,
				borderRadius: radii.sm,
				cursor: "pointer",
				transition: transitions.all,
				background: isSelected ? colors.mintGlow : isHovered ? colors.surface : "transparent"
			},
			children: [
				/* @__PURE__ */ jsx("span", {
					style: {
						width: "16px",
						height: "16px",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						flexShrink: 0
					},
					children: hasChildren && /* @__PURE__ */ jsx(ChevronIcon, { expanded: isExpanded })
				}),
				node.icon && /* @__PURE__ */ jsx("span", {
					style: {
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						flexShrink: 0
					},
					children: node.icon
				}),
				/* @__PURE__ */ jsx("span", {
					style: {
						flex: 1,
						fontFamily: fonts.mono,
						fontSize: "13px",
						color: isSelected ? colors.mint : colors.text,
						transition: transitions.all,
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis"
					},
					children: node.label
				})
			]
		}), isExpanded && hasChildren && /* @__PURE__ */ jsx("div", {
			role: "group",
			children: node.children.map((child) => /* @__PURE__ */ jsx(TreeNodeItem, {
				node: child,
				level: level + 1,
				selectedId,
				onSelect,
				expandedIds,
				onToggle
			}, child.id))
		})]
	});
}
const TreeView = forwardRef(function TreeView({ nodes, onSelect, selectedId, style }, ref) {
	const [expandedIds, setExpandedIds] = useState(/* @__PURE__ */ new Set());
	const handleToggle = useCallback((id) => {
		setExpandedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}, []);
	const resolvedStyle = style ?? {};
	return /* @__PURE__ */ jsx("div", {
		ref,
		role: "tree",
		style: {
			fontFamily: fonts.mono,
			...resolvedStyle
		},
		children: nodes.map((node) => /* @__PURE__ */ jsx(TreeNodeItem, {
			node,
			level: 0,
			selectedId,
			onSelect,
			expandedIds,
			onToggle: handleToggle
		}, node.id))
	});
});
//#endregion
//#region molecules/ThemeSwitcher.tsx
const CATEGORIES = [
	{
		key: "dark",
		label: "Dark"
	},
	{
		key: "light",
		label: "Light"
	},
	{
		key: "contrast",
		label: "Kontrast"
	}
];
function ThemeSwitcher({ activeThemeId, onThemeChange }) {
	const [activeCategory, setActiveCategory] = useState("dark");
	const [hoveredTab, setHoveredTab] = useState(null);
	const { colors: uiColors } = useTheme();
	const filtered = THEMES.filter((t) => t.meta.category === activeCategory);
	return /* @__PURE__ */ jsxs("div", {
		style: {
			display: "flex",
			flexDirection: "column",
			gap: "12px"
		},
		children: [/* @__PURE__ */ jsx("div", {
			style: {
				display: "flex",
				gap: "4px",
				background: uiColors.surface,
				borderRadius: "8px",
				padding: "4px"
			},
			children: CATEGORIES.map(({ key, label }) => {
				const isActive = activeCategory === key;
				const isHovered = hoveredTab === key;
				return /* @__PURE__ */ jsx("button", {
					type: "button",
					onClick: () => setActiveCategory(key),
					onMouseEnter: () => setHoveredTab(key),
					onMouseLeave: () => setHoveredTab(null),
					style: {
						flex: 1,
						padding: "6px 8px",
						border: "none",
						borderRadius: "6px",
						cursor: "pointer",
						fontFamily: fonts.mono,
						fontSize: "11px",
						fontWeight: isActive ? 600 : 400,
						transition: transitions.fast,
						background: isActive ? uiColors.mint : isHovered ? uiColors.surfaceElevated : "transparent",
						color: isActive ? uiColors.void : uiColors.textMuted
					},
					children: label
				}, key);
			})
		}), /* @__PURE__ */ jsx("div", {
			style: {
				display: "flex",
				flexWrap: "wrap",
				gap: "8px"
			},
			children: filtered.map((t) => /* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					gap: "4px"
				},
				children: [/* @__PURE__ */ jsx(ThemeSwatch, {
					theme: t,
					active: activeThemeId === t.meta.id,
					size: "md",
					onClick: onThemeChange
				}), /* @__PURE__ */ jsx("span", {
					style: {
						fontSize: "9px",
						fontFamily: fonts.mono,
						color: uiColors.textMuted,
						textTransform: "uppercase",
						letterSpacing: "0.05em"
					},
					children: t.meta.name
				})]
			}, t.meta.id))
		})]
	});
}
//#endregion
//#region molecules/BottomSheet.tsx
function BottomSheet({ isOpen, onClose, children, title, dragHandle }) {
	const [isAnimatingOut, setIsAnimatingOut] = useState(false);
	const sheetRef = useRef(null);
	const dragState = useRef(null);
	useEffect(() => {
		if (isOpen) setIsAnimatingOut(false);
	}, [isOpen]);
	useEffect(() => {
		if (!isOpen) return;
		const vv = window.visualViewport;
		if (!vv) return;
		const update = () => document.documentElement.style.setProperty("--vvp-height", `${vv.height}px`);
		vv.addEventListener("resize", update);
		vv.addEventListener("scroll", update);
		update();
		return () => {
			vv.removeEventListener("resize", update);
			vv.removeEventListener("scroll", update);
			document.documentElement.style.removeProperty("--vvp-height");
		};
	}, [isOpen]);
	const close = useCallback(() => setIsAnimatingOut(true), []);
	const onDragDown = (e) => {
		if (e.pointerType === "mouse") return;
		dragState.current = {
			startY: e.clientY,
			currentY: e.clientY
		};
		e.currentTarget.setPointerCapture(e.pointerId);
	};
	const onDragMove = (e) => {
		if (!dragState.current) return;
		const delta = e.clientY - dragState.current.startY;
		if (delta < 0) return;
		dragState.current.currentY = e.clientY;
		if (sheetRef.current) {
			sheetRef.current.style.transform = `translateY(${delta}px)`;
			sheetRef.current.style.transition = "none";
		}
	};
	const onDragUp = () => {
		if (!dragState.current) return;
		const delta = dragState.current.currentY - dragState.current.startY;
		dragState.current = null;
		if (sheetRef.current) {
			sheetRef.current.style.transition = "";
			sheetRef.current.style.transform = "";
		}
		if (delta > 80) close();
	};
	const handleAnimationEnd = () => {
		if (isAnimatingOut) {
			onClose();
			setIsAnimatingOut(false);
		}
	};
	if (!isOpen && !isAnimatingOut) return null;
	const backdropStyle = {
		position: "fixed",
		inset: 0,
		background: "rgba(0,0,0,0.7)",
		backdropFilter: "blur(4px)",
		WebkitBackdropFilter: "blur(4px)",
		zIndex: zIndices.modal,
		animation: isAnimatingOut ? `bs-bd-out ${durations.fast} ease-out forwards` : `bs-bd-in ${durations.fast} ease-out forwards`
	};
	const sheetStyle = {
		position: "fixed",
		bottom: 0,
		left: 0,
		right: 0,
		borderRadius: `${radii["2xl"]} ${radii["2xl"]} 0 0`,
		maxHeight: "calc(var(--vvp-height, 100dvh) - 20px)",
		zIndex: zIndices.modal + 1,
		background: colors.glassBg,
		backdropFilter: "blur(20px)",
		WebkitBackdropFilter: "blur(20px)",
		border: `1px solid ${colors.glassBorder}`,
		borderBottom: "none",
		display: "flex",
		flexDirection: "column",
		overflow: "hidden",
		animation: isAnimatingOut ? `bs-slide-out ${durations.slow} cubic-bezier(0.4,0,1,1) forwards` : `bs-slide-in ${durations.slow} cubic-bezier(0.32,0.72,0,1) forwards`
	};
	return /* @__PURE__ */ jsxs(Fragment, { children: [
		/* @__PURE__ */ jsx("style", { children: `
        @keyframes bs-bd-in   { from{opacity:0} to{opacity:1} }
        @keyframes bs-bd-out  { from{opacity:1} to{opacity:0} }
        @keyframes bs-slide-in  { from{transform:translateY(100%)} to{transform:translateY(0)} }
        @keyframes bs-slide-out { from{transform:translateY(0)} to{transform:translateY(110%)} }
      ` }),
		/* @__PURE__ */ jsx("div", {
			style: backdropStyle,
			onPointerUp: (e) => {
				if (e.target === e.currentTarget) close();
			}
		}),
		/* @__PURE__ */ jsxs("div", {
			ref: sheetRef,
			role: "dialog",
			"aria-modal": "true",
			"aria-label": title ?? void 0,
			style: sheetStyle,
			onAnimationEnd: handleAnimationEnd,
			children: [
				(dragHandle ?? false) && /* @__PURE__ */ jsx("div", {
					onPointerDown: onDragDown,
					onPointerMove: onDragMove,
					onPointerUp: onDragUp,
					style: {
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						minHeight: 48,
						touchAction: "none",
						cursor: "grab",
						flexShrink: 0
					},
					children: /* @__PURE__ */ jsx("div", { style: {
						width: 36,
						height: 4,
						borderRadius: 2,
						background: colors.border
					} })
				}),
				title !== null && /* @__PURE__ */ jsx("div", {
					style: {
						padding: `${spacing[4]} ${spacing[4]} ${spacing[3]}`,
						borderBottom: `1px solid ${colors.glassBorder}`,
						flexShrink: 0
					},
					children: /* @__PURE__ */ jsx("h2", {
						style: {
							margin: 0,
							fontFamily: fonts.sans,
							fontSize: fontSizes.lg,
							fontWeight: fontWeights.semibold,
							color: colors.text,
							lineHeight: 1.25
						},
						children: title
					})
				}),
				/* @__PURE__ */ jsx("div", {
					style: {
						flex: 1,
						overflowY: "auto",
						paddingBottom: "calc(8px + var(--safe-bottom, 0px))"
					},
					children
				})
			]
		})
	] });
}
//#endregion
export { StatDisplay as a, Modal as i, ThemeSwitcher as n, FormField as o, TreeView as r, Tabs as s, BottomSheet as t };

//# sourceMappingURL=BottomSheet-Cdx2XRCw.mjs.map